import { useState, useEffect, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Download, Upload, FileSpreadsheet, Pencil, Trash2, AlertTriangle, Image, X, Search, RefreshCw } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

// Lista de bancos en México
const BANCOS_MEXICO = [
  'BBVA México',
  'Santander México',
  'Banorte',
  'Citibanamex',
  'HSBC México',
  'Scotiabank',
  'Banco Azteca',
  'Inbursa',
  'Banco del Bajío',
  'Banregio',
  'Afirme',
  'Multiva',
  'Banca Mifel',
  'Banco Autofin',
  'BanCoppel',
  'Banco Compartamos',
  'Banco Inmobiliario Mexicano',
  'Bansi',
  'CI Banco',
  'Intercam Banco',
  'Monex',
  'Actinver',
  'Invex',
  'Ve Por Más',
  'Banco Base',
  'Banco Forjadores',
  'Banco Sabadell',
  'Bancrea',
  'ABC Capital',
  'Consubanco',
  'Banco del Bienestar',
  'American Express',
  'Bank of America',
  'JP Morgan',
  'Deutsche Bank',
  'Otro'
];

const MONEDAS = ['MXN', 'USD', 'EUR', 'GBP', 'CAD', 'JPY', 'CHF'];

const Catalogs = () => {
  const [companies, setCompanies] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogs, setDialogs] = useState({ 
    company: false, account: false, vendor: false, customer: false, 
    editAccount: false, editVendor: false, editCustomer: false, editCompany: false 
  });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, item: null });
  
  const [companyForm, setCompanyForm] = useState({ nombre: '', rfc: '', moneda_base: 'MXN', pais: 'México', logo_url: null });
  const [editingCompany, setEditingCompany] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [accountForm, setAccountForm] = useState({ nombre: '', numero_cuenta: '', banco: '', moneda: 'MXN', saldo_inicial: 0, fecha_saldo: '' });
  const [editingAccount, setEditingAccount] = useState(null);
  const [vendorForm, setVendorForm] = useState({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_pago: '', tasa_iva: '', retencion_tipo: '' });
  const [editingVendor, setEditingVendor] = useState(null);
  const [customerForm, setCustomerForm] = useState({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_cobranza: '', tasa_iva: '', retencion_tipo: '' });
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [importing, setImporting] = useState(false);
  const [searchVendors, setSearchVendors] = useState('');
  const [searchCustomers, setSearchCustomers] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [companiesRes, accountsRes, vendorsRes, customersRes] = await Promise.all([
        api.get('/companies'),
        api.get('/bank-accounts'),
        api.get('/vendors'),
        api.get('/customers')
      ]);
      setCompanies(companiesRes.data);
      setBankAccounts(accountsRes.data);
      setVendors(vendorsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Error cargando catálogos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      await api.post('/companies', companyForm);
      toast.success('Empresa creada');
      setDialogs({ ...dialogs, company: false });
      loadData();
      setCompanyForm({ nombre: '', rfc: '', moneda_base: 'MXN', pais: 'México', logo_url: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando empresa');
    }
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      nombre: company.nombre,
      rfc: company.rfc,
      moneda_base: company.moneda_base || 'MXN',
      pais: company.pais || 'México',
      logo_url: company.logo_url || null
    });
    setDialogs({ ...dialogs, editCompany: true });
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/companies/${editingCompany.id}`, companyForm);
      console.log('Company updated:', response.data);
      toast.success('Empresa actualizada');
      setDialogs({ ...dialogs, editCompany: false });
      setEditingCompany(null);
      setCompanyForm({ nombre: '', rfc: '', moneda_base: 'MXN', pais: 'México', logo_url: null });
      // Refresh data immediately
      const companiesRes = await api.get('/companies');
      console.log('Companies refreshed:', companiesRes.data);
      setCompanies([...companiesRes.data]); // Force new array reference
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando empresa');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingCompany) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no debe exceder 2MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/companies/${editingCompany.id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCompanyForm({ ...companyForm, logo_url: response.data.logo_url });
      toast.success('Logo subido correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error subiendo logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!editingCompany) return;
    
    try {
      await api.delete(`/companies/${editingCompany.id}/logo`);
      setCompanyForm({ ...companyForm, logo_url: null });
      toast.success('Logo eliminado');
    } catch (error) {
      toast.error('Error eliminando logo');
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        ...accountForm, 
        saldo_inicial: parseFloat(accountForm.saldo_inicial),
        fecha_saldo: accountForm.fecha_saldo || null
      };
      await api.post('/bank-accounts', payload);
      toast.success('Cuenta bancaria creada');
      setDialogs({ ...dialogs, account: false });
      loadData();
      setAccountForm({ nombre: '', numero_cuenta: '', banco: '', moneda: 'MXN', saldo_inicial: 0, fecha_saldo: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando cuenta');
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setAccountForm({
      nombre: account.nombre,
      numero_cuenta: account.numero_cuenta,
      banco: account.banco,
      moneda: account.moneda,
      saldo_inicial: account.saldo_inicial,
      fecha_saldo: account.fecha_saldo ? account.fecha_saldo.split('T')[0] : ''
    });
    setDialogs({ ...dialogs, editAccount: true });
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    if (!editingAccount) return;
    
    try {
      await api.put(`/bank-accounts/${editingAccount.id}`, { 
        ...accountForm, 
        saldo_inicial: parseFloat(accountForm.saldo_inicial),
        fecha_saldo: accountForm.fecha_saldo || null
      });
      toast.success('Cuenta bancaria actualizada');
      setDialogs({ ...dialogs, editAccount: false });
      setEditingAccount(null);
      setAccountForm({ nombre: '', numero_cuenta: '', banco: '', moneda: 'MXN', saldo_inicial: 0, fecha_saldo: '' });
      // Refresh data
      const accountsRes = await api.get('/bank-accounts');
      setBankAccounts(accountsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando cuenta');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirm.item || deleteConfirm.type !== 'account') return;
    
    try {
      await api.delete(`/bank-accounts/${deleteConfirm.item.id}`);
      toast.success('Cuenta bancaria eliminada');
      setDeleteConfirm({ open: false, type: null, item: null });
      // Refresh data
      const accountsRes = await api.get('/bank-accounts');
      setBankAccounts(accountsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando cuenta');
    }
  };

  // ===== VENDOR HANDLERS =====
  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      nombre: vendor.nombre,
      rfc: vendor.rfc || '',
      email: vendor.email || '',
      telefono: vendor.telefono || '',
      direccion: vendor.direccion || '',
      plazo_pago: vendor.plazo_pago ?? 0,
      tasa_iva: vendor.tasa_iva ?? '',
      retencion_tipo: vendor.retencion_tipo ?? ''
    });
    setDialogs({ ...dialogs, editVendor: true });
  };

  const handleUpdateVendor = async (e) => {
    e.preventDefault();
    if (!editingVendor) return;
    
    try {
      await api.put(`/vendors/${editingVendor.id}`, {
        ...vendorForm,
        plazo_pago: vendorForm.plazo_pago === '' ? null : vendorForm.plazo_pago,
        tasa_iva: vendorForm.tasa_iva === '' ? null : Number(vendorForm.tasa_iva),
        retencion_tipo: vendorForm.retencion_tipo === '' ? null : vendorForm.retencion_tipo,
      });
      toast.success('Proveedor actualizado');
      setDialogs({ ...dialogs, editVendor: false });
      setEditingVendor(null);
      setVendorForm({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_pago: '', tasa_iva: '', retencion_tipo: '' });
      // Refresh data
      const vendorsRes = await api.get('/vendors');
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando proveedor');
    }
  };

  const handleDeleteVendor = async () => {
    if (!deleteConfirm.item || deleteConfirm.type !== 'vendor') return;
    
    try {
      await api.delete(`/vendors/${deleteConfirm.item.id}`);
      toast.success('Proveedor eliminado');
      setDeleteConfirm({ open: false, type: null, item: null });
      // Refresh data
      const vendorsRes = await api.get('/vendors');
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando proveedor');
    }
  };

  // ===== CUSTOMER HANDLERS =====
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      nombre: customer.nombre,
      rfc: customer.rfc || '',
      email: customer.email || '',
      telefono: customer.telefono || '',
      direccion: customer.direccion || '',
      plazo_cobranza: customer.plazo_cobranza ?? 0,
      tasa_iva: customer.tasa_iva ?? '',
      retencion_tipo: customer.retencion_tipo ?? ''
    });
    setDialogs({ ...dialogs, editCustomer: true });
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;
    
    try {
      await api.put(`/customers/${editingCustomer.id}`, {
        ...customerForm,
        plazo_cobranza: customerForm.plazo_cobranza === '' ? null : customerForm.plazo_cobranza,
        tasa_iva: customerForm.tasa_iva === '' ? null : Number(customerForm.tasa_iva),
        retencion_tipo: customerForm.retencion_tipo === '' ? null : customerForm.retencion_tipo,
      });
      toast.success('Cliente actualizado');
      setDialogs({ ...dialogs, editCustomer: false });
      setEditingCustomer(null);
      setCustomerForm({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_cobranza: '', tasa_iva: '', retencion_tipo: '' });
      // Refresh data
      const customersRes = await api.get('/customers');
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando cliente');
    }
  };

  const handleDeleteCustomer = async () => {
    if (!deleteConfirm.item || deleteConfirm.type !== 'customer') return;
    
    try {
      await api.delete(`/customers/${deleteConfirm.item.id}`);
      toast.success('Cliente eliminado');
      setDeleteConfirm({ open: false, type: null, item: null });
      // Refresh data
      const customersRes = await api.get('/customers');
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando cliente');
    }
  };

  const handleDeleteCompany = async () => {
    if (!deleteConfirm.item || deleteConfirm.type !== 'company') return;
    
    try {
      await api.delete(`/companies/${deleteConfirm.item.id}`);
      toast.success('Empresa eliminada');
      setDeleteConfirm({ open: false, type: null, item: null });
      // Refresh data
      const companiesRes = await api.get('/companies');
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando empresa');
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    try {
      await api.post('/vendors', {
        ...vendorForm,
        plazo_pago: vendorForm.plazo_pago === '' ? null : vendorForm.plazo_pago,
        tasa_iva: vendorForm.tasa_iva === '' ? null : Number(vendorForm.tasa_iva),
        retencion_tipo: vendorForm.retencion_tipo === '' ? null : vendorForm.retencion_tipo,
      });
      toast.success('Proveedor creado');
      setDialogs({ ...dialogs, vendor: false });
      loadData();
      setVendorForm({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_pago: '', tasa_iva: '', retencion_tipo: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando proveedor');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      await api.post('/customers', {
        ...customerForm,
        plazo_cobranza: customerForm.plazo_cobranza === '' ? null : customerForm.plazo_cobranza,
        tasa_iva: customerForm.tasa_iva === '' ? null : Number(customerForm.tasa_iva),
        retencion_tipo: customerForm.retencion_tipo === '' ? null : customerForm.retencion_tipo,
      });
      toast.success('Cliente creado');
      setDialogs({ ...dialogs, customer: false });
      loadData();
      setCustomerForm({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_cobranza: '', tasa_iva: '', retencion_tipo: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando cliente');
    }
  };

  const handleImportVendorsFromCfdis = async () => {
    setImporting(true);
    try {
      const res = await api.post('/vendors/import-from-cfdis');
      const { importados, existentes } = res.data;
      if (importados > 0) {
        toast.success(`${importados} proveedor${importados !== 1 ? 'es' : ''} importado${importados !== 1 ? 's' : ''} desde CFDIs. ${existentes} ya existían.`);
      } else {
        toast.info(`No hay proveedores nuevos. ${existentes} ya estaban en el catálogo.`);
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error importando proveedores desde CFDIs');
    } finally {
      setImporting(false);
    }
  };

  const handleImportCustomersFromCfdis = async () => {
    setImporting(true);
    try {
      const res = await api.post('/customers/import-from-cfdis');
      const { importados, existentes } = res.data;
      if (importados > 0) {
        toast.success(`${importados} cliente${importados !== 1 ? 's' : ''} importado${importados !== 1 ? 's' : ''} desde CFDIs. ${existentes} ya existían.`);
      } else {
        toast.info(`No hay clientes nuevos. ${existentes} ya estaban en el catálogo.`);
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error importando clientes desde CFDIs');
    } finally {
      setImporting(false);
    }
  };

  // Template downloads
  const downloadVendorsTemplate = async () => {
    try {
      const response = await api.get('/vendors/template', { responseType: 'blob' });
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_proveedores.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error descargando plantilla');
    }
  };

  const downloadCustomersTemplate = async () => {
    try {
      const response = await api.get('/customers/template', { responseType: 'blob' });
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_clientes.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error descargando plantilla');
    }
  };

  // Import handlers
  const handleImportVendors = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/vendors/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { imported, updated, errors } = response.data;
      toast.success(`${imported} proveedores importados, ${updated} actualizados`);
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} errores: ${errors.slice(0, 2).join(', ')}`);
      }
      loadData();
    } catch (error) {
      toast.error('Error importando proveedores');
    }
    e.target.value = '';
  };

  const handleImportCustomers = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/customers/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { imported, updated, errors } = response.data;
      toast.success(`${imported} clientes importados, ${updated} actualizados`);
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} errores: ${errors.slice(0, 2).join(', ')}`);
      }
      loadData();
    } catch (error) {
      toast.error('Error importando clientes');
    }
    e.target.value = '';
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="p-8 space-y-6" data-testid="catalogs-page">
      <PageHeader title="Catálogos" subtitle="Gestión de empresas, cuentas, proveedores y clientes" />

      <Tabs defaultValue="companies" className="w-full">
        <TabsList>
          <TabsTrigger value="companies" data-testid="tab-companies">Empresas</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">Cuentas Bancarias</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Proveedores</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Clientes</TabsTrigger>
        </TabsList>

        <TabsContent value="companies">
          <Card className="border-[#E2E8F0]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Empresas</CardTitle>
                <CardDescription>{companies.length} empresas registradas</CardDescription>
              </div>
              <Dialog open={dialogs.company} onOpenChange={(open) => setDialogs({...dialogs, company: open})}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F172A] gap-2" data-testid="create-company-button">
                    <Plus size={16} /> Nueva Empresa
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nueva Empresa</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCompany} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nombre</Label>
                      <Input data-testid="company-nombre-input" value={companyForm.nombre} onChange={(e) => setCompanyForm({...companyForm, nombre: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label>RFC</Label>
                      <Input data-testid="company-rfc-input" value={companyForm.rfc} onChange={(e) => setCompanyForm({...companyForm, rfc: e.target.value})} required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Moneda Base</Label>
                        <Input value={companyForm.moneda_base} onChange={(e) => setCompanyForm({...companyForm, moneda_base: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                        <Label>País</Label>
                        <Input value={companyForm.pais} onChange={(e) => setCompanyForm({...companyForm, pais: e.target.value})} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" data-testid="company-submit-button">Crear Empresa</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table className="data-table" key={`companies-table-${companies.length}-${companies[0]?.nombre || ''}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Logo</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>RFC</TableHead>
                    <TableHead>Moneda</TableHead>
                    <TableHead>País</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={`${company.id}-${company.nombre}`}>
                      <TableCell>
                        {company.logo_url ? (
                          <img src={company.logo_url} alt="Logo" className="w-10 h-10 object-contain rounded" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                            <Image size={16} className="text-gray-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{company.nombre}</TableCell>
                      <TableCell className="mono">{company.rfc}</TableCell>
                      <TableCell>{company.moneda_base}</TableCell>
                      <TableCell>{company.pais}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditCompany(company)}
                            data-testid={`edit-company-${company.id}`}
                          >
                            <Pencil size={16} className="text-blue-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setDeleteConfirm({ open: true, type: 'company', item: company })}
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 size={16} className="text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Edit Company Dialog - Must be inside companies TabsContent */}
          <Dialog open={dialogs.editCompany} onOpenChange={(open) => {
            setDialogs({...dialogs, editCompany: open});
            if (!open) {
              setEditingCompany(null);
              setCompanyForm({ nombre: '', rfc: '', moneda_base: 'MXN', pais: 'México', logo_url: null });
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Editar Empresa</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateCompany} className="space-y-4">
                {/* Logo Section */}
                <div className="space-y-2">
                  <Label>Logo de la Empresa</Label>
                  <div className="flex items-center gap-4">
                    {companyForm.logo_url ? (
                      <div className="relative">
                        <img 
                          src={companyForm.logo_url} 
                          alt="Logo" 
                          className="w-24 h-24 object-contain border rounded-lg bg-white p-2"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6"
                          onClick={handleRemoveLogo}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                        <Image size={32} className="text-gray-400" />
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="gap-2"
                      >
                        <Upload size={14} />
                        {uploadingLogo ? 'Subiendo...' : 'Subir Logo'}
                      </Button>
                      <p className="text-xs text-gray-500">PNG, JPG hasta 2MB</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Nombre de la Empresa</Label>
                  <Input 
                    value={companyForm.nombre} 
                    onChange={(e) => setCompanyForm({...companyForm, nombre: e.target.value})} 
                    placeholder="Ej: Mi Empresa S.A. de C.V."
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label>RFC</Label>
                  <Input 
                    value={companyForm.rfc} 
                    onChange={(e) => setCompanyForm({...companyForm, rfc: e.target.value.toUpperCase()})} 
                    placeholder="Ej: ABC123456XYZ"
                    maxLength={13}
                    required 
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Moneda Base</Label>
                    <Select value={companyForm.moneda_base} onValueChange={(v) => setCompanyForm({...companyForm, moneda_base: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar moneda" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONEDAS.map(moneda => (
                          <SelectItem key={moneda} value={moneda}>{moneda}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>País</Label>
                    <Input 
                      value={companyForm.pais} 
                      onChange={(e) => setCompanyForm({...companyForm, pais: e.target.value})} 
                      placeholder="México"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogs({...dialogs, editCompany: false})}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#0F172A]">Guardar Cambios</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Delete Company Confirmation Dialog */}
          <AlertDialog open={deleteConfirm.open && deleteConfirm.type === 'company'} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, item: null })}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  ¿Eliminar empresa?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. Se eliminará permanentemente la empresa 
                  <strong> "{deleteConfirm.item?.nombre}"</strong> con RFC {deleteConfirm.item?.rfc}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteCompany}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="accounts">
          <Card className="border-[#E2E8F0]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Cuentas Bancarias</CardTitle>
                <CardDescription>{bankAccounts.length} cuentas registradas</CardDescription>
              </div>
              <Dialog open={dialogs.account} onOpenChange={(open) => setDialogs({...dialogs, account: open})}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F172A] gap-2" data-testid="create-account-button">
                    <Plus size={16} /> Nueva Cuenta
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nueva Cuenta Bancaria</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateAccount} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nombre de la Cuenta</Label>
                      <Input data-testid="account-nombre-input" value={accountForm.nombre} onChange={(e) => setAccountForm({...accountForm, nombre: e.target.value})} placeholder="Ej: Cuenta Principal" required />
                    </div>
                    <div className="space-y-2">
                      <Label>Banco</Label>
                      <Select value={accountForm.banco} onValueChange={(v) => setAccountForm({...accountForm, banco: v})}>
                        <SelectTrigger data-testid="account-banco-input">
                          <SelectValue placeholder="Seleccionar banco" />
                        </SelectTrigger>
                        <SelectContent>
                          {BANCOS_MEXICO.map(banco => (
                            <SelectItem key={banco} value={banco}>{banco}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Número de Cuenta</Label>
                      <Input data-testid="account-numero-input" value={accountForm.numero_cuenta} onChange={(e) => setAccountForm({...accountForm, numero_cuenta: e.target.value})} placeholder="Ej: 0123456789" required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Moneda</Label>
                        <Select value={accountForm.moneda} onValueChange={(v) => setAccountForm({...accountForm, moneda: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar moneda" />
                          </SelectTrigger>
                          <SelectContent>
                            {MONEDAS.map(moneda => (
                              <SelectItem key={moneda} value={moneda}>{moneda}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Saldo Inicial</Label>
                        <Input type="number" step="0.01" value={accountForm.saldo_inicial} onChange={(e) => setAccountForm({...accountForm, saldo_inicial: e.target.value})} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha del Saldo Inicial</Label>
                      <Input 
                        type="date" 
                        value={accountForm.fecha_saldo} 
                        onChange={(e) => setAccountForm({...accountForm, fecha_saldo: e.target.value})} 
                      />
                      <p className="text-xs text-muted-foreground">Fecha a partir de la cual aplica este saldo para los reportes</p>
                    </div>
                    <DialogFooter>
                      <Button type="submit" data-testid="account-submit-button">Crear Cuenta</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Banco</TableHead>
                    <TableHead>Número</TableHead>
                    <TableHead>Moneda</TableHead>
                    <TableHead>Saldo Inicial</TableHead>
                    <TableHead>Fecha Saldo</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bankAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell className="font-medium">{account.nombre}</TableCell>
                      <TableCell>{account.banco}</TableCell>
                      <TableCell className="mono">{account.numero_cuenta}</TableCell>
                      <TableCell>{account.moneda}</TableCell>
                      <TableCell className="mono">${account.saldo_inicial?.toLocaleString('es-MX') || '0'}</TableCell>
                      <TableCell className="text-xs">
                        {account.fecha_saldo ? new Date(account.fecha_saldo).toLocaleDateString('es-MX') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditAccount(account)}
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            data-testid={`edit-account-${account.id}`}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setDeleteConfirm({ open: true, type: 'account', item: account })}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                            data-testid={`delete-account-${account.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Edit Account Dialog */}
          <Dialog open={dialogs.editAccount} onOpenChange={(open) => {
            setDialogs({...dialogs, editAccount: open});
            if (!open) {
              setEditingAccount(null);
              setAccountForm({ nombre: '', numero_cuenta: '', banco: '', moneda: 'MXN', saldo_inicial: 0, fecha_saldo: '' });
            }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Cuenta Bancaria</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateAccount} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nombre de la Cuenta</Label>
                  <Input value={accountForm.nombre} onChange={(e) => setAccountForm({...accountForm, nombre: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Banco</Label>
                  <Select value={accountForm.banco} onValueChange={(v) => setAccountForm({...accountForm, banco: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar banco" />
                    </SelectTrigger>
                    <SelectContent>
                      {BANCOS_MEXICO.map(banco => (
                        <SelectItem key={banco} value={banco}>{banco}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Número de Cuenta</Label>
                  <Input value={accountForm.numero_cuenta} onChange={(e) => setAccountForm({...accountForm, numero_cuenta: e.target.value})} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Moneda</Label>
                    <Select value={accountForm.moneda} onValueChange={(v) => setAccountForm({...accountForm, moneda: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar moneda" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONEDAS.map(moneda => (
                          <SelectItem key={moneda} value={moneda}>{moneda}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Saldo Inicial</Label>
                    <Input type="number" step="0.01" value={accountForm.saldo_inicial} onChange={(e) => setAccountForm({...accountForm, saldo_inicial: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Fecha del Saldo Inicial</Label>
                  <Input 
                    type="date" 
                    value={accountForm.fecha_saldo} 
                    onChange={(e) => setAccountForm({...accountForm, fecha_saldo: e.target.value})} 
                  />
                  <p className="text-xs text-muted-foreground">Fecha a partir de la cual aplica este saldo para los reportes</p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogs({...dialogs, editAccount: false})}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#0F172A]">Guardar Cambios</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={deleteConfirm.open && deleteConfirm.type === 'account'} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, item: null })}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  ¿Eliminar cuenta bancaria?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. Se eliminará permanentemente la cuenta 
                  <strong> "{deleteConfirm.item?.nombre}"</strong> del banco {deleteConfirm.item?.banco}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteAccount}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="vendors">
          <Card className="border-[#E2E8F0]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-orange-600">🏢</span> Proveedores
                </CardTitle>
                <CardDescription>{vendors.length} proveedores registrados</CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={handleImportVendorsFromCfdis}
                  disabled={importing}
                  data-testid="import-vendors-cfdis-button"
                >
                  <RefreshCw size={16} className={importing ? 'animate-spin' : ''} />
                  Importar desde CFDIs
                </Button>
                <Button variant="outline" className="gap-2" onClick={downloadVendorsTemplate} data-testid="download-vendors-template">
                  <Download size={16} />
                  Plantilla
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => document.getElementById('vendors-import').click()} data-testid="import-vendors-button">
                  <Upload size={16} />
                  Importar Excel
                </Button>
                <input
                  id="vendors-import"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleImportVendors}
                />
                <Dialog open={dialogs.vendor} onOpenChange={(open) => setDialogs({...dialogs, vendor: open})}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#0F172A] gap-2" data-testid="create-vendor-button">
                      <Plus size={16} /> Nuevo Proveedor
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Nuevo Proveedor</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateVendor} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nombre</Label>
                          <Input data-testid="vendor-nombre-input" value={vendorForm.nombre} onChange={(e) => setVendorForm({...vendorForm, nombre: e.target.value})} required />
                        </div>
                        <div className="space-y-2">
                          <Label>RFC</Label>
                          <Input data-testid="vendor-rfc-input" value={vendorForm.rfc} onChange={(e) => setVendorForm({...vendorForm, rfc: e.target.value.toUpperCase()})} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Dirección</Label>
                        <Input value={vendorForm.direccion} onChange={(e) => setVendorForm({...vendorForm, direccion: e.target.value})} placeholder="Calle, número, colonia, ciudad, estado, CP" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input type="email" value={vendorForm.email} onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                          <Label>Teléfono</Label>
                          <Input value={vendorForm.telefono} onChange={(e) => setVendorForm({...vendorForm, telefono: e.target.value})} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Plazo de Pago (días)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={vendorForm.plazo_pago}
                            onChange={(e) => setVendorForm({...vendorForm, plazo_pago: e.target.value === '' ? '' : parseInt(e.target.value)})}
                            placeholder="30"
                          />
                          <p className="text-xs text-muted-foreground">Días para el aging de CxP</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Tasa de IVA (sus facturas)</Label>
                          <select
                            value={vendorForm.tasa_iva}
                            onChange={(e) => setVendorForm({...vendorForm, tasa_iva: e.target.value})}
                            className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                            data-testid="vendor-tasa-iva"
                          >
                            <option value="">Sin configurar (regla general)</option>
                            <option value="16">16% — General</option>
                            <option value="8">8% — Región fronteriza</option>
                            <option value="0">0% — Sin IVA / exento</option>
                          </select>
                          <p className="text-xs text-muted-foreground">Usada en el IVA acreditable (Planeación Fiscal)</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Régimen de retención (al pagarle)</Label>
                        <select
                          value={vendorForm.retencion_tipo}
                          onChange={(e) => setVendorForm({...vendorForm, retencion_tipo: e.target.value})}
                          className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                          data-testid="vendor-retencion"
                        >
                          <option value="">Sin retención</option>
                          <option value="honorarios_pf">Honorarios PF — ret. ISR 10% + IVA 2/3</option>
                          <option value="arrendamiento_pf">Arrendamiento PF — ret. ISR 10% + IVA 2/3</option>
                          <option value="fletes">Fletes / autotransporte — ret. IVA 4%</option>
                          <option value="resico_pf">RESICO PF (servicios) — ret. ISR 1.25% + IVA 2/3</option>
                        </select>
                        <p className="text-xs text-muted-foreground">Las retenciones se calculan en Planeación Fiscal y se enteran al SAT el día 17</p>
                      </div>
                      <DialogFooter>
                        <Button type="submit" data-testid="vendor-submit-button" className="bg-[#0F172A]">Crear Proveedor</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {vendors.length === 0 ? (
                <div className="text-center py-12">
                  <FileSpreadsheet size={48} className="mx-auto text-[#94A3B8] mb-4" />
                  <p className="text-[#64748B] mb-4">No hay proveedores registrados</p>
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={handleImportVendorsFromCfdis} disabled={importing}>
                      <RefreshCw size={16} className={importing ? 'animate-spin' : ''} /> Importar desde CFDIs
                    </Button>
                    <Button variant="outline" className="gap-2" onClick={downloadVendorsTemplate}>
                      <Download size={16} /> Descargar Plantilla
                    </Button>
                    <Button className="bg-[#0F172A] gap-2" onClick={() => document.getElementById('vendors-import').click()}>
                      <Upload size={16} /> Importar Excel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative mb-3">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre o RFC..."
                      value={searchVendors}
                      onChange={e => setSearchVendors(e.target.value)}
                      className="pl-9 h-9 text-sm"
                    />
                  </div>
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>RFC</TableHead>
                        <TableHead>Plazo Pago</TableHead>
                        <TableHead>IVA</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendors
                        .filter(v => {
                          const q = searchVendors.toLowerCase();
                          return !q || v.nombre.toLowerCase().includes(q) || (v.rfc || '').toLowerCase().includes(q);
                        })
                        .map((vendor) => (
                          <TableRow key={vendor.id}>
                            <TableCell className="font-medium">{vendor.nombre}</TableCell>
                            <TableCell className="mono">{vendor.rfc || '-'}</TableCell>
                            <TableCell>{vendor.plazo_pago ?? 0} días</TableCell>
                            <TableCell>
                              {vendor.tasa_iva == null
                                ? <span className="text-slate-400 text-xs">general</span>
                                : <span className="font-mono text-xs">{vendor.tasa_iva}%</span>}
                            </TableCell>
                            <TableCell>{vendor.email || '-'}</TableCell>
                            <TableCell>{vendor.telefono || '-'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleEditVendor(vendor)}
                                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  data-testid={`edit-vendor-${vendor.id}`}
                                >
                                  <Pencil size={14} />
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => setDeleteConfirm({ open: true, type: 'vendor', item: vendor })}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                                  data-testid={`delete-vendor-${vendor.id}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          {/* Edit Vendor Dialog - Inside vendors TabsContent */}
          <Dialog open={dialogs.editVendor} onOpenChange={(open) => {
            setDialogs({...dialogs, editVendor: open});
            if (!open) {
              setEditingVendor(null);
              setVendorForm({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_pago: '', tasa_iva: '', retencion_tipo: '' });
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Editar Proveedor</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateVendor} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={vendorForm.nombre} onChange={(e) => setVendorForm({...vendorForm, nombre: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>RFC</Label>
                    <Input value={vendorForm.rfc} onChange={(e) => setVendorForm({...vendorForm, rfc: e.target.value.toUpperCase()})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input value={vendorForm.direccion} onChange={(e) => setVendorForm({...vendorForm, direccion: e.target.value})} placeholder="Calle, número, colonia, ciudad, estado, CP" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={vendorForm.email} onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input value={vendorForm.telefono} onChange={(e) => setVendorForm({...vendorForm, telefono: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Plazo de Pago (días)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={vendorForm.plazo_pago}
                      onChange={(e) => setVendorForm({...vendorForm, plazo_pago: e.target.value === '' ? '' : parseInt(e.target.value)})}
                      placeholder="30"
                    />
                    <p className="text-xs text-muted-foreground">Días para el aging de CxP</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Tasa de IVA (sus facturas)</Label>
                    <select
                      value={vendorForm.tasa_iva}
                      onChange={(e) => setVendorForm({...vendorForm, tasa_iva: e.target.value})}
                      className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                    >
                      <option value="">Sin configurar (regla general)</option>
                      <option value="16">16% — General</option>
                      <option value="8">8% — Región fronteriza</option>
                      <option value="0">0% — Sin IVA / exento</option>
                    </select>
                    <p className="text-xs text-muted-foreground">Usada en el IVA acreditable (Planeación Fiscal)</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Régimen de retención (al pagarle)</Label>
                  <select
                    value={vendorForm.retencion_tipo}
                    onChange={(e) => setVendorForm({...vendorForm, retencion_tipo: e.target.value})}
                    className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                  >
                    <option value="">Sin retención</option>
                    <option value="honorarios_pf">Honorarios PF — ret. ISR 10% + IVA 2/3</option>
                    <option value="arrendamiento_pf">Arrendamiento PF — ret. ISR 10% + IVA 2/3</option>
                    <option value="fletes">Fletes / autotransporte — ret. IVA 4%</option>
                    <option value="resico_pf">RESICO PF (servicios) — ret. ISR 1.25% + IVA 2/3</option>
                  </select>
                  <p className="text-xs text-muted-foreground">Las retenciones se calculan en Planeación Fiscal y se enteran al SAT el día 17</p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogs({...dialogs, editVendor: false})}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#0F172A]">Guardar Cambios</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Delete Vendor Confirmation - Inside vendors TabsContent */}
          <AlertDialog open={deleteConfirm.open && deleteConfirm.type === 'vendor'} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, item: null })}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  ¿Eliminar proveedor?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. Se eliminará permanentemente el proveedor 
                  <strong> "{deleteConfirm.item?.nombre}"</strong>.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteVendor}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        <TabsContent value="customers">
          <Card className="border-[#E2E8F0]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-blue-600">👤</span> Clientes
                </CardTitle>
                <CardDescription>{customers.length} clientes registrados</CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={handleImportCustomersFromCfdis}
                  disabled={importing}
                  data-testid="import-customers-cfdis-button"
                >
                  <RefreshCw size={16} className={importing ? 'animate-spin' : ''} />
                  Importar desde CFDIs
                </Button>
                <Button variant="outline" className="gap-2" onClick={downloadCustomersTemplate} data-testid="download-customers-template">
                  <Download size={16} />
                  Plantilla
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => document.getElementById('customers-import').click()} data-testid="import-customers-button">
                  <Upload size={16} />
                  Importar Excel
                </Button>
                <input
                  id="customers-import"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleImportCustomers}
                />
                <Dialog open={dialogs.customer} onOpenChange={(open) => setDialogs({...dialogs, customer: open})}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#0F172A] gap-2" data-testid="create-customer-button">
                      <Plus size={16} /> Nuevo Cliente
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Nuevo Cliente</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCustomer} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nombre</Label>
                          <Input data-testid="customer-nombre-input" value={customerForm.nombre} onChange={(e) => setCustomerForm({...customerForm, nombre: e.target.value})} required />
                        </div>
                        <div className="space-y-2">
                          <Label>RFC</Label>
                          <Input data-testid="customer-rfc-input" value={customerForm.rfc} onChange={(e) => setCustomerForm({...customerForm, rfc: e.target.value.toUpperCase()})} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Dirección</Label>
                        <Input value={customerForm.direccion} onChange={(e) => setCustomerForm({...customerForm, direccion: e.target.value})} placeholder="Calle, número, colonia, ciudad, estado, CP" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})} />
                        </div>
                        <div className="space-y-2">
                          <Label>Teléfono</Label>
                          <Input value={customerForm.telefono} onChange={(e) => setCustomerForm({...customerForm, telefono: e.target.value})} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Plazo de Cobranza (días)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={customerForm.plazo_cobranza}
                            onChange={(e) => setCustomerForm({...customerForm, plazo_cobranza: e.target.value === '' ? '' : parseInt(e.target.value)})}
                            placeholder="30"
                          />
                          <p className="text-xs text-muted-foreground">Días para el aging de CxC</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Tasa de IVA (sus ventas)</Label>
                          <select
                            value={customerForm.tasa_iva}
                            onChange={(e) => setCustomerForm({...customerForm, tasa_iva: e.target.value})}
                            className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                            data-testid="customer-tasa-iva"
                          >
                            <option value="">Sin configurar (usa % global)</option>
                            <option value="16">16% — General</option>
                            <option value="8">8% — Región fronteriza</option>
                            <option value="0">0% — Tasa cero / Exento</option>
                          </select>
                          <p className="text-xs text-muted-foreground">Usada en Planeación Fiscal (IVA cobrado)</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Retención que te aplica este cliente (si eres PF)</Label>
                        <select
                          value={customerForm.retencion_tipo}
                          onChange={(e) => setCustomerForm({...customerForm, retencion_tipo: e.target.value})}
                          className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                          data-testid="customer-retencion"
                        >
                          <option value="">No me retiene</option>
                          <option value="honorarios_pf">Honorarios PF — me retiene ISR 10% + IVA 2/3</option>
                          <option value="arrendamiento_pf">Arrendamiento PF — me retiene ISR 10% + IVA 2/3</option>
                          <option value="fletes">Fletes / autotransporte — me retiene IVA 4%</option>
                          <option value="resico_pf">RESICO PF — me retiene ISR 1.25% + IVA 2/3</option>
                        </select>
                        <p className="text-xs text-muted-foreground">Las retenciones reales se leen del CFDI; esto se usa para proyectar meses futuros</p>
                      </div>
                      <DialogFooter>
                        <Button type="submit" data-testid="customer-submit-button" className="bg-[#0F172A]">Crear Cliente</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {customers.length === 0 ? (
                <div className="text-center py-12">
                  <FileSpreadsheet size={48} className="mx-auto text-[#94A3B8] mb-4" />
                  <p className="text-[#64748B] mb-4">No hay clientes registrados</p>
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={handleImportCustomersFromCfdis} disabled={importing}>
                      <RefreshCw size={16} className={importing ? 'animate-spin' : ''} /> Importar desde CFDIs
                    </Button>
                    <Button variant="outline" className="gap-2" onClick={downloadCustomersTemplate}>
                      <Download size={16} /> Descargar Plantilla
                    </Button>
                    <Button className="bg-[#0F172A] gap-2" onClick={() => document.getElementById('customers-import').click()}>
                      <Upload size={16} /> Importar Excel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative mb-3">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre o RFC..."
                      value={searchCustomers}
                      onChange={e => setSearchCustomers(e.target.value)}
                      className="pl-9 h-9 text-sm"
                    />
                  </div>
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>RFC</TableHead>
                        <TableHead>Plazo Cobranza</TableHead>
                        <TableHead>IVA</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customers
                        .filter(c => {
                          const q = searchCustomers.toLowerCase();
                          return !q || c.nombre.toLowerCase().includes(q) || (c.rfc || '').toLowerCase().includes(q);
                        })
                        .map((customer) => (
                          <TableRow key={customer.id}>
                            <TableCell className="font-medium">{customer.nombre}</TableCell>
                            <TableCell className="mono">{customer.rfc || '-'}</TableCell>
                            <TableCell>{customer.plazo_cobranza ?? 0} días</TableCell>
                            <TableCell>
                              {customer.tasa_iva == null
                                ? <span className="text-slate-400 text-xs">global</span>
                                : <span className="font-mono text-xs">{customer.tasa_iva}%</span>}
                            </TableCell>
                            <TableCell>{customer.email || '-'}</TableCell>
                            <TableCell>{customer.telefono || '-'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleEditCustomer(customer)}
                                  className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  data-testid={`edit-customer-${customer.id}`}
                                >
                                  <Pencil size={14} />
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => setDeleteConfirm({ open: true, type: 'customer', item: customer })}
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                                  data-testid={`delete-customer-${customer.id}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          {/* Edit Vendor Dialog */}
          <Dialog open={dialogs.editVendor} onOpenChange={(open) => {
            setDialogs({...dialogs, editVendor: open});
            if (!open) {
              setEditingVendor(null);
              setVendorForm({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_pago: '', tasa_iva: '', retencion_tipo: '' });
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Editar Proveedor</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateVendor} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={vendorForm.nombre} onChange={(e) => setVendorForm({...vendorForm, nombre: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>RFC</Label>
                    <Input value={vendorForm.rfc} onChange={(e) => setVendorForm({...vendorForm, rfc: e.target.value.toUpperCase()})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input value={vendorForm.direccion} onChange={(e) => setVendorForm({...vendorForm, direccion: e.target.value})} placeholder="Calle, número, colonia, ciudad, estado, CP" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={vendorForm.email} onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input value={vendorForm.telefono} onChange={(e) => setVendorForm({...vendorForm, telefono: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Plazo de Pago (días)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={vendorForm.plazo_pago}
                      onChange={(e) => setVendorForm({...vendorForm, plazo_pago: e.target.value === '' ? '' : parseInt(e.target.value)})}
                      placeholder="30"
                    />
                    <p className="text-xs text-muted-foreground">Días para el aging de CxP</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Tasa de IVA (sus facturas)</Label>
                    <select
                      value={vendorForm.tasa_iva}
                      onChange={(e) => setVendorForm({...vendorForm, tasa_iva: e.target.value})}
                      className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                    >
                      <option value="">Sin configurar (regla general)</option>
                      <option value="16">16% — General</option>
                      <option value="8">8% — Región fronteriza</option>
                      <option value="0">0% — Sin IVA / exento</option>
                    </select>
                    <p className="text-xs text-muted-foreground">Usada en el IVA acreditable (Planeación Fiscal)</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Régimen de retención (al pagarle)</Label>
                  <select
                    value={vendorForm.retencion_tipo}
                    onChange={(e) => setVendorForm({...vendorForm, retencion_tipo: e.target.value})}
                    className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                  >
                    <option value="">Sin retención</option>
                    <option value="honorarios_pf">Honorarios PF — ret. ISR 10% + IVA 2/3</option>
                    <option value="arrendamiento_pf">Arrendamiento PF — ret. ISR 10% + IVA 2/3</option>
                    <option value="fletes">Fletes / autotransporte — ret. IVA 4%</option>
                    <option value="resico_pf">RESICO PF (servicios) — ret. ISR 1.25% + IVA 2/3</option>
                  </select>
                  <p className="text-xs text-muted-foreground">Las retenciones se calculan en Planeación Fiscal y se enteran al SAT el día 17</p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogs({...dialogs, editVendor: false})}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#0F172A]">Guardar Cambios</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Customer Dialog */}
          <Dialog open={dialogs.editCustomer} onOpenChange={(open) => {
            setDialogs({...dialogs, editCustomer: open});
            if (!open) {
              setEditingCustomer(null);
              setCustomerForm({ nombre: '', rfc: '', email: '', telefono: '', direccion: '', plazo_cobranza: '', tasa_iva: '' });
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Editar Cliente</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateCustomer} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={customerForm.nombre} onChange={(e) => setCustomerForm({...customerForm, nombre: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>RFC</Label>
                    <Input value={customerForm.rfc} onChange={(e) => setCustomerForm({...customerForm, rfc: e.target.value.toUpperCase()})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input value={customerForm.direccion} onChange={(e) => setCustomerForm({...customerForm, direccion: e.target.value})} placeholder="Calle, número, colonia, ciudad, estado, CP" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={customerForm.email} onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input value={customerForm.telefono} onChange={(e) => setCustomerForm({...customerForm, telefono: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Plazo de Cobranza (días)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={customerForm.plazo_cobranza}
                      onChange={(e) => setCustomerForm({...customerForm, plazo_cobranza: e.target.value === '' ? '' : parseInt(e.target.value)})}
                      placeholder="30"
                    />
                    <p className="text-xs text-muted-foreground">Días para el aging de CxC</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Tasa de IVA (sus ventas)</Label>
                    <select
                      value={customerForm.tasa_iva}
                      onChange={(e) => setCustomerForm({...customerForm, tasa_iva: e.target.value})}
                      className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                      data-testid="customer-tasa-iva-edit"
                    >
                      <option value="">Sin configurar (usa % global)</option>
                      <option value="16">16% — General</option>
                      <option value="8">8% — Región fronteriza</option>
                      <option value="0">0% — Tasa cero / Exento</option>
                    </select>
                    <p className="text-xs text-muted-foreground">Usada en Planeación Fiscal (IVA cobrado)</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Retención que te aplica este cliente (si eres PF)</Label>
                  <select
                    value={customerForm.retencion_tipo}
                    onChange={(e) => setCustomerForm({...customerForm, retencion_tipo: e.target.value})}
                    className="w-full h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
                    data-testid="customer-retencion-edit"
                  >
                    <option value="">No me retiene</option>
                    <option value="honorarios_pf">Honorarios PF — me retiene ISR 10% + IVA 2/3</option>
                    <option value="arrendamiento_pf">Arrendamiento PF — me retiene ISR 10% + IVA 2/3</option>
                    <option value="fletes">Fletes / autotransporte — me retiene IVA 4%</option>
                    <option value="resico_pf">RESICO PF — me retiene ISR 1.25% + IVA 2/3</option>
                  </select>
                  <p className="text-xs text-muted-foreground">Las retenciones reales se leen del CFDI; esto se usa para proyectar meses futuros</p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogs({...dialogs, editCustomer: false})}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#0F172A]">Guardar Cambios</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Delete Vendor Confirmation */}
          <AlertDialog open={deleteConfirm.open && deleteConfirm.type === 'vendor'} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, item: null })}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  ¿Eliminar proveedor?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. Se eliminará permanentemente el proveedor 
                  <strong> "{deleteConfirm.item?.nombre}"</strong>.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteVendor}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Delete Customer Confirmation */}
          <AlertDialog open={deleteConfirm.open && deleteConfirm.type === 'customer'} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, item: null })}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  ¿Eliminar cliente?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción no se puede deshacer. Se eliminará permanentemente el cliente 
                  <strong> "{deleteConfirm.item?.nombre}"</strong>.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDeleteCustomer}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Catalogs;
